// Online play in the browser (2026-11-01): the socket to a room (worker/rooms.ts, through /api/room on the site), the
// lobby the lobby card draws, the commands you send (paced under the room's CMD_RATE so none is dropped), and the
// Lockstep that main.ts steps the sim through. main.ts owns the sim and the bots; this owns the wire. No DOM.
// A dropped socket reconnects with the seat's token and the turns it already has, so the room sends only what it
// missed; a reload rejoins the same way from sessionStorage (and replays the match from tick 0, as a late joiner does).
import { Lockstep } from '../net/lockstep';
import { CMD_RATE, cleanCommand, isCode, newCode, type Seat, type Start, type ToClient, type ToServer } from '../net/protocol';
import type { HeroId } from '../sim/defs';
import type { Command } from '../sim/sim';

declare const __NET_BUILD__: string | undefined;
/** The sim and net code this page runs (scripts/net-build.ts, set by the build); 'dev' when run from source. */
export const NET_BUILD = typeof __NET_BUILD__ === 'string' ? __NET_BUILD__ : 'dev';

export type NetStatus = 'idle' | 'finding' | 'connecting' | 'lobby' | 'playing' | 'closed';
export type LobbyView = {
  status: NetStatus; code: string | null; you: number; host: number; seats: Seat[]; quick: boolean;
  /** When the quick room starts, on this page's clock (performance.now()), or null. */
  startsAt: number | null;
  /** The last thing to tell the player (a full room, a closed match), or null. */
  message: string | null;
};
export interface NetHooks {
  /** The lobby changed: redraw it. */
  changed(): void;
  /** A match to play from tick 0 (a start, a late join, a reload). main.ts builds the sim and its bots. */
  start(start: Start, you: number): void;
  /** The room saw two hashes for one tick. */
  desync(t: number): void;
}

const SESSION_KEY = 'footmen-wars.room';
/** Under the room's limit (protocol.ts CMD_RATE), so network bunching never pushes a burst over it. */
const SEND_RATE = CMD_RATE - 2;
/** Reconnect waits, in ms; together they stay inside GRACE_MS, so the seat is still yours when it works. */
const RETRY_MS = [500, 1000, 2000, 3000, 5000, 7000, 8000, 8000, 8000]; // 42.5 s, inside GRACE_MS's 45 (Flash final F3)

export class Online {
  view: LobbyView = Online.blank();
  ls: Lockstep | null = null;
  private ws: WebSocket | null = null;
  private token: string | undefined;
  private queue: Command[] = [];
  private sent: number[] = [];
  private tries = 0;
  private live = false; // we want a socket open (false after leave)
  private quickTries = 0;
  private retry = 0; // the pending reconnect's timer (Flash F7: a stale one opened a second socket after Leave)
  private gen = 0; // bumped by drop(): an answer from before it belongs to a search that is over (Flash final F5)

  constructor(private hooks: NetHooks, private origin = location.origin) {}

  static blank(): LobbyView { return { status: 'idle', code: null, you: -1, host: -1, seats: [], quick: false, startsAt: null, message: null }; }
  get active() { return this.view.status !== 'idle' && this.view.status !== 'closed'; }
  get playing() { return this.view.status === 'playing' && this.ls !== null; }
  /** The invite link for this room: the game with ?room=CODE. */
  link() { return this.view.code ? `${this.origin}${location.pathname}?room=${this.view.code}` : ''; }

  /** Quick match: the room the matchmaker names. `again` is a retry after that room filled in the instant before. */
  async quick(again = false) {
    if (!again) this.quickTries = 0;
    this.reset('finding');
    const gen = this.gen;
    try {
      const r = await fetch(`${this.origin}/api/quick`, { method: 'POST', body: JSON.stringify({ build: NET_BUILD }) });
      if (!r.ok) throw new Error(String(r.status));
      const { code } = (await r.json()) as { code: string };
      if (gen !== this.gen || this.view.status !== 'finding') return; // left, or searched again, while it looked
      this.open(code, true);
    } catch { if (gen === this.gen && this.view.status === 'finding') this.fail('Online play is not reachable right now. Try again in a minute, or play vs bots.'); } // not if they left or moved on (Flash F6)
  }
  create() { this.reset('connecting'); this.open(newCode(Math.random), false); }
  join(code: string, token?: string) {
    const c = code.trim().toUpperCase();
    if (!isCode(c)) { this.view.message = 'A room code is 5 letters and numbers.'; this.hooks.changed(); return; }
    this.reset('connecting'); this.token = token; this.open(c, false);
  }
  /** A room this tab was in before a reload, if any. */
  static saved(): { code: string; token: string } | null {
    try { const v = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null'); return v && isCode(v.code) && typeof v.token === 'string' ? v : null; } catch { return null; }
  }

  hero(id: HeroId) { this.say({ k: 'hero', id }); }
  begin() { this.say({ k: 'start' }); }
  /** A command for your seat: it reaches the sim when its turn comes back from the room, on every device at once. */
  cmd(c: Command) {
    if (!this.playing || !cleanCommand(c)) return false;
    this.queue.push(c); this.flush();
    return true;
  }
  /** Every frame: send what the rate allows. */
  flush(now = performance.now()) {
    while (this.sent.length && now - this.sent[0] >= 1000) this.sent.shift();
    while (this.queue.length && this.sent.length < SEND_RATE && this.ws?.readyState === WebSocket.OPEN) { this.say({ k: 'cmd', c: this.queue.shift()! }); this.sent.push(now); }
  }
  hash(t: number, h: string) { this.say({ k: 'hash', t, h }); }

  /** Leave for good: the room hands the seat to a bot at once. */
  leave() {
    this.say({ k: 'leave' });
    this.drop(); this.forget();
    this.view = Online.blank();
  }

  /** Done with this room: no socket, no retry, and nothing of its match kept (Flash F1: a closed room's match and seat
   *  token made the next room's start look like a resend, so that match never began). */
  private drop() {
    this.live = false; clearTimeout(this.retry); this.gen++;
    try { this.ws?.close(1000); } catch { /* already */ }
    this.ws = null; this.ls = null; this.queue = []; this.token = undefined;
  }
  /** No rejoin after a reload: kept through reset (a reload's own rejoin goes through it), dropped when a room ends. */
  private forget() { try { sessionStorage.removeItem(SESSION_KEY); } catch { /* none */ } }
  private reset(status: NetStatus) {
    this.drop();
    this.view = { ...Online.blank(), status };
    this.live = true; this.tries = 0;
    this.hooks.changed();
  }
  private fail(m: string) {
    this.drop(); this.forget();
    this.view = { ...this.view, status: 'closed', message: m };
    this.hooks.changed();
  }
  private say(m: ToServer) { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m)); }

  private open(code: string, quick: boolean) {
    this.view.code = code; this.view.quick = quick;
    clearTimeout(this.retry);
    const was = this.ws; this.ws = null; // its handlers check this.ws, so closing it changes nothing here
    try { was?.close(1000); } catch { /* already */ }
    const ws = new WebSocket(`${this.origin.replace(/^http/, 'ws')}/api/room/${code}${quick ? '?quick=1' : ''}`);
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) { try { ws.close(1000); } catch { /* already */ } return; }
      ws.send(JSON.stringify({ k: 'hello', name: '', build: NET_BUILD, ...(this.token ? { token: this.token } : {}), ...(this.ls ? { have: this.ls.latest + 1 } : {}) } satisfies ToServer));
    };
    ws.onmessage = (e) => { if (this.ws === ws) { try { this.on(JSON.parse(String(e.data)) as ToClient); } catch { /* not ours */ } } };
    ws.onclose = (e) => {
      if (this.ws !== ws || !this.live) return;
      this.ws = null;
      const inMatch = this.view.status === 'playing';
      // a quick room that filled between the matchmaker's answer and our hello: ask for the next one
      if (this.view.quick && !inMatch && this.view.message && this.quickTries++ < 3) return void this.quick(true);
      // this seat opened on another tab or device: that one plays now, so this one stops instead of taking it back
      if (e.reason === 'replaced') return this.fail('This match is open somewhere else now.');
      // closed before we ever had a seat and with no word why (a hello the room waited too long for): try again
      const unseated = e.reason === 'bye' && this.view.you < 0 && !this.view.message;
      if (e.reason === 'over' || (e.reason === 'bye' && !unseated) || (this.view.message && !inMatch))
        return this.fail(this.view.message ?? (inMatch ? 'The match has ended.' : 'The room has closed.'));
      if (this.tries >= RETRY_MS.length) return this.fail('Lost the connection to the room.');
      this.retry = window.setTimeout(() => { if (this.live && !this.ws && this.view.code) this.open(this.view.code, this.view.quick); }, RETRY_MS[this.tries++]);
    };
  }

  private on(m: ToClient) {
    const v = this.view;
    switch (m.k) {
      case 'room': {
        this.tries = 0; // seated: a later drop gets its retries again (not on open: a room closing every socket before its hello would loop, Flash F2)
        // a room that is a lobby again while we play: the server restarted and the match is gone
        if (v.status === 'playing' && m.phase === 'lobby') { this.leave(); return this.fail('The match was lost when its server restarted. Sorry about that.'); }
        this.token = m.token;
        try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ code: m.code, token: m.token })); } catch { /* reload won't rejoin */ }
        Object.assign(v, { code: m.code, you: m.you, host: m.host, seats: m.seats, quick: m.quick, message: null,
          startsAt: m.startsAt ? performance.now() + (m.startsAt - m.now) : null,
          status: m.phase === 'lobby' ? 'lobby' : 'playing' } satisfies Partial<LobbyView>);
        this.hooks.changed();
        return;
      }
      case 'start': {
        v.status = 'playing';
        // a page with a match keeps its sim and loads only what it missed (Flash F2: a resend from 0 rebuilt the match)
        if (!this.ls) { this.ls = new Lockstep(m.start); this.ls.load(m.turns, m.at); this.hooks.start(m.start, v.you); }
        else this.ls.load(m.turns, m.at);
        return;
      }
      case 't': this.ls?.add(m); return;
      case 'desync': this.hooks.desync(m.t); return;
      case 'err': v.message = m.m; this.hooks.changed(); return;
    }
  }
}

